import React, { PureComponent } from "react";
import { connect } from "react-redux";
import { MetaComponentPaths } from "MetaComponent/MetaComponent";
import { Grid, Paper, Tabs, Tab } from "@material-ui/core";
import { withStyles } from "@material-ui/core/styles";
import ComponentTargets from "./components/ComponentTargets/ComponentTargets";
import DirectoryExplorerSelector from "MetaComponent/selectors/DirectoryExplorer";
import FarFieldWaveFront from "./components/FarFieldWavefront/FarFieldWavefront";
import NearFieldWaveFront from "./components/NearFieldWavefront/NearFieldWavefront";
import DesignTargetDetails from "./components/DesignTargetDetails/DesignTargetDetails";
import SetPointSelector from "MetaComponent/selectors/SetPoint";
import SelectedDesignTargetSelector from "MetaComponent/selectors/SelectedDesignTarget";
import SelectedDesignTargetApi from "MetaComponent/api/SelectedDesignTarget";
import DesignTargetApi from "MetaComponent/api/DesignTarget";
import FFWTTargetSelector from "MetaComponent/selectors/FFWTTarget";
import DesignTargetSelector from "MetaComponent/selectors/DesignTarget";
import Spinner from "components/Spinner/Spinner";
import { withErrorBoundary } from "BaseApp/ErrorBoundary/ErrorBoundary";
import SetPointApi from "MetaComponent/api/SetPoint";

const styles = theme => ({
  root: {
    flexGrow: 1,
    width: "99%"
  },
  paper: {
    padding: theme.spacing(2)
  },
  tab: {
    textTransform: "none",
    textAlign: "left"
  }
});

/**
 * A component created to be the content for Meta Cell component's target.
 * @author Akira Kotsugai
 * @param {Object} props - the props passed by parent components
 */
export class TargetCanvas extends PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      focusedDesignTargetId: 0,
      selectedTabIndex: 0,
      preloadedWavefront: null
    };
  }

  /**
   * it sets the open page for this component as soon as the component mounts
   */
  componentDidMount() {
    const { fetchSetPointsAction } = this.props;
    this.props.setPage(MetaComponentPaths.TARGET);
    fetchSetPointsAction();
  }

  /**
   * whenever a different target is selected in the UI,
   * we decide on which wavefront to show
   */
  componentDidUpdate = async (prevProps, prevState) => {
    const { designTargets } = this.props,
      { focusedDesignTargetId } = this.state,
      focusedDesignTargetChanged =
        prevState.focusedDesignTargetId !== focusedDesignTargetId;
    if (focusedDesignTargetChanged && focusedDesignTargetId) {
      const ffwfTarget = designTargets.byId[focusedDesignTargetId].FFWFTarget;
      const wavefront = await this.ensureThatWavefrontDataIsLoaded();
      if (
        ffwfTarget &&
        (!wavefront ||
          (wavefront?.amplitude === null && wavefront?.phase === null))
      ) {
        this.setState({ selectedTabIndex: 1 });
      }
    }
  };

  /**
   * Loads the wavefront data for the focused design target.
   * If a wavefront is found, sets selectedTabIndex to 0.
   * @param {boolean} showStaticRepresentation
   */
  ensureThatWavefrontDataIsLoaded = async () => {
    const { focusedDesignTargetId } = this.state;
    const { designTargets } = this.props;
    const focusedDesignTarget = designTargets.byId[focusedDesignTargetId];

    if (!focusedDesignTarget) {
      this.setState({ preloadedWavefront: null });
      return;
    }

    let wavefront = null;
    if (!focusedDesignTarget.NFWaveFront) {
      wavefront = await DesignTargetApi.requestNFWFTargetImagesWavefront(
        focusedDesignTarget.id
      );
    } else {
      wavefront = designTargets.byId[focusedDesignTargetId].NFWaveFront;
    }

    if (
      wavefront &&
      wavefront?.amplitude !== null &&
      wavefront?.phase !== null
    ) {
      this.setState({
        selectedTabIndex: 0,
        preloadedWavefront: !focusedDesignTarget.NFWaveFront ? wavefront : null
      });
    } else {
      this.setState({ preloadedWavefront: null });
    }
    return wavefront;
  };

  /**
   * @returns {Object} the open meta component entity
   */
  getOpenMetaComponent = () => {
    const { openMetaComponentId, metaComponents } = this.props;
    return metaComponents.byId[openMetaComponentId];
  };

  /**
   * it changes the focused design target state
   * @param {Number} id - the design target id to be focused
   * @callback
   */
  focusOnDesignTarget = id => {
    this.setState({
      focusedDesignTargetId: id
    });
  };

  /**
   * it gets the id of the available set points in the relationship between
   * the open meta component and its design targets.
   * @returns {Number[]} - the available set points' ids
   */
  getCompatibleSetPointsIds = () => {
    const { focusedDesignTargetId } = this.state;
    let compatibleSetPoints = new Set([]);
    const metaComponent = this.getOpenMetaComponent();
    if (focusedDesignTargetId) {
      const { selectedDesignTargets } = this.props;
      for (const selectedDesignTarget of Object.values(
        selectedDesignTargets.byId
      )) {
        if (selectedDesignTarget.list_of_set_points) {
          const list_of_set_points = selectedDesignTarget.list_of_set_points.filter(
            sp => sp !== undefined
          );
          if (list_of_set_points.length > 0) {
            list_of_set_points.forEach(point => compatibleSetPoints.add(point));
          }
          if (
            selectedDesignTarget.set_point &&
            !compatibleSetPoints.has(selectedDesignTarget.set_point) &&
            !Boolean(metaComponent.ideal_family)
          ) {
            compatibleSetPoints.add(selectedDesignTarget.set_point);
          }
        }
      }
    }
    return [...compatibleSetPoints];
  };

  /**
   * it updates the selected design target entity with the new properties
   * @param {Object} properties - the new properties
   */
  updateSelectedDesignTarget = properties => {
    const { selectedDesignTargets, updateSelectedDesignTarget } = this.props,
      selectedDesignTarget = Object.values(selectedDesignTargets.byId).find(
        sdt => sdt.design_target === this.state.focusedDesignTargetId
      );
    return updateSelectedDesignTarget(selectedDesignTarget.id, properties);
  };

  /**
   * Callback to handle the tab change event
   */
  handleTabChange = (event, newValue) => {
    this.setState({
      selectedTabIndex: newValue
    });
  };

  render() {
    const { ready, classes } = this.props;
    const { selectedTabIndex, focusedDesignTargetId } = this.state;
    if (!ready) {
      return (
        <div className={classes.progress}>
          <Spinner name="Waiting" size={68} timeout={30000} />
        </div>
      );
    }

    return (
      <div className={classes.root}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Paper className={classes.paper}>
              <ComponentTargets
                compatibleSetPointsIds={this.getCompatibleSetPointsIds()}
                focusedDesignTargetId={focusedDesignTargetId}
                focusOnDesignTarget={this.focusOnDesignTarget}
              />
            </Paper>
          </Grid>
        </Grid>

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <DesignTargetDetails
              compatibleSetPointsIds={this.getCompatibleSetPointsIds()}
              focusedDesignTargetId={focusedDesignTargetId}
              openMetaComponent={this.getOpenMetaComponent()}
              updateSelectedDesignTarget={this.updateSelectedDesignTarget}
            />
          </Grid>
        </Grid>

        <Grid container spacing={2}>
          <Grid item xs={12}>
            {focusedDesignTargetId !== 0 && (
              <Paper className={classes.paper}>
                <Tabs
                  classes={{ root: classes.tabs }}
                  value={selectedTabIndex}
                  indicatorColor="primary"
                  textColor="primary"
                  onChange={this.handleTabChange}
                  aria-label="far-field and near-field wavefront targets"
                >
                  <Tab
                    classes={{ root: classes.tab }}
                    label="Near-field Wavefront"
                  />
                  <Tab
                    classes={{ root: classes.tab }}
                    label="Far-field Wavefront"
                  />
                </Tabs>
                {selectedTabIndex === 0 && (
                  <NearFieldWaveFront
                    focusedDesignTargetId={this.state.focusedDesignTargetId}
                    openMetaComponent={this.getOpenMetaComponent()}
                    preloadedWavefront={this.state.preloadedWavefront}
                  />
                )}
                {selectedTabIndex === 1 && (
                  <FarFieldWaveFront
                    focusedDesignTargetId={this.state.focusedDesignTargetId}
                    ffwfTargets={this.props.ffwfTargets}
                    designTargets={this.props.designTargets}
                    openMetaComponent={this.getOpenMetaComponent()}
                  />
                )}
              </Paper>
            )}
          </Grid>
        </Grid>
      </div>
    );
  }
}

const mapStateToProps = state => {
  return {
    openMetaComponentId: DirectoryExplorerSelector.getMetaComponentOpenId(
      state
    ),
    metaComponents: DirectoryExplorerSelector.getMetaComponents(state),
    setPoints: SetPointSelector.getSetPoints(state),
    selectedDesignTargets: SelectedDesignTargetSelector.getSelectedDesignTargets(
      state
    ),
    ffwfTargets: FFWTTargetSelector.getFFWTTargets(state),
    designTargets: DesignTargetSelector.getDesignTargets(state)
  };
};

const mapDispatchToProps = dispatch => {
  return {
    updateSelectedDesignTarget: (id, newProperties) =>
      dispatch(
        SelectedDesignTargetApi.updateSelectedDesignTarget(id, newProperties)
      ),
    fetchSetPointsAction: () => dispatch(SetPointApi.fetchSetPoints())
  };
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withErrorBoundary(withStyles(styles)(TargetCanvas)));
